import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/context";
import {
  fetchNotifications,
  markAllRead,
  markRead,
  type Notification,
} from "../notifications/api";
import { renderNotification } from "../notifications/renderers";
import {
  contactRequestErrorMessage,
  decideContactRequest,
} from "../contacts/api";
import { decideShare, shareErrorMessage } from "../shares/api";

// /notifications — full inbox.  Cursor-paginated; we batch-mark visible
// items as read when the page mounts so the bell badge reflects reality
// after opening it.  (A future refinement: only mark as read once the row
// scrolls into view, but for now "opened the page = read" is fine.)

export function Notifications() {
  const { state } = useAuth();
  const accessToken =
    (state.status === "authenticated" && state.session.access_token) ||
    (state.status === "offline" && state.session.access_token) ||
    null;

  const [items, setItems] = useState<Notification[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const loadFirst = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    setError(null);
    try {
      const page = await fetchNotifications(accessToken, { limit: 30 });
      setItems(page.items);
      setNextCursor(page.nextCursor);
      setUnreadCount(page.unreadCount);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  const loadMore = async () => {
    if (!accessToken || !nextCursor) return;
    setLoading(true);
    try {
      const page = await fetchNotifications(accessToken, {
        cursor: nextCursor,
        limit: 30,
      });
      setItems((prev) => [...prev, ...page.items]);
      setNextCursor(page.nextCursor);
      setUnreadCount(page.unreadCount);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadFirst();
  }, [loadFirst]);

  const onMarkAllRead = async () => {
    if (!accessToken) return;
    try {
      await markAllRead(accessToken);
      setItems((prev) =>
        prev.map((n) => (n.readAt ? n : { ...n, readAt: new Date().toISOString() })),
      );
      setUnreadCount(0);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const onMarkOne = async (id: string) => {
    if (!accessToken) return;
    try {
      await markRead(accessToken, id);
      setItems((prev) =>
        prev.map((n) =>
          n.id === id && !n.readAt
            ? { ...n, readAt: new Date().toISOString() }
            : n,
        ),
      );
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <main className="mx-auto max-w-2xl px-6 py-8">
      <div className="mb-4 flex items-baseline justify-between">
        <h1 className="text-xl font-semibold tracking-tight">
          Notifications
          {unreadCount > 0 && (
            <span className="ml-2 text-sm font-normal text-slate-500 dark:text-slate-400">
              {unreadCount} unread
            </span>
          )}
        </h1>
        <div className="flex items-center gap-3 text-sm">
          {unreadCount > 0 && (
            <button
              type="button"
              onClick={() => void onMarkAllRead()}
              className="text-slate-500 underline hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
            >
              Mark all read
            </button>
          )}
          <Link
            to="/"
            className="text-slate-500 underline hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
          >
            Back
          </Link>
        </div>
      </div>

      {error && (
        <p className="mb-3 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-300">
          {error}
        </p>
      )}

      <ul className="space-y-1.5">
        {items.map((n) => {
          const r = renderNotification(n);
          const unread = !n.readAt;
          const isRequest =
            n.type === "contact_request" &&
            typeof (n.payload as { requestId?: string }).requestId === "string";
          const isShareOffer =
            n.type === "share_offered" &&
            typeof (n.payload as { shareId?: string }).shareId === "string";
          const reportLinkId =
            n.type === "report_addressed" &&
            typeof (n.payload as { reportId?: string }).reportId === "string"
              ? (n.payload as { reportId: string }).reportId
              : null;
          const activityItemId =
            n.type === "share_activity" &&
            typeof (n.payload as { itemId?: string }).itemId === "string"
              ? (n.payload as { itemId: string }).itemId
              : null;
          return (
            <li
              key={n.id}
              className={`flex items-start gap-3 rounded-lg border px-3 py-2 ${
                unread
                  ? "border-slate-300 bg-white dark:border-slate-600 dark:bg-slate-900"
                  : "border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-900"
              }`}
            >
              <span className="select-none text-xl leading-none" aria-hidden>
                {r.icon}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2">
                  <span
                    className={`text-sm ${unread ? "font-medium text-slate-900 dark:text-slate-100" : "text-slate-700 dark:text-slate-200"}`}
                  >
                    {r.title}
                  </span>
                  <time className="shrink-0 text-[11px] text-slate-400 dark:text-slate-500">
                    {formatWhen(n.createdAt)}
                  </time>
                </div>
                {r.body && (
                  <div className="mt-0.5 text-sm text-slate-700 dark:text-slate-200">{r.body}</div>
                )}
                {r.meta && (
                  <div className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
                    {r.meta}
                  </div>
                )}
                {isRequest && unread && accessToken && (
                  <ContactRequestActions
                    token={accessToken}
                    requestId={
                      (n.payload as { requestId: string }).requestId
                    }
                    onDecided={() => {
                      // Server marks the notification read in the same tx;
                      // mirror locally so the row de-highlights immediately.
                      setItems((prev) =>
                        prev.map((x) =>
                          x.id === n.id
                            ? { ...x, readAt: new Date().toISOString() }
                            : x,
                        ),
                      );
                      setUnreadCount((c) => Math.max(0, c - 1));
                    }}
                  />
                )}
                {reportLinkId && (
                  <Link
                    to={`/reports#${reportLinkId}`}
                    className="mt-1 inline-block text-[11px] font-medium text-indigo-600 hover:underline dark:text-indigo-400"
                  >
                    View report →
                  </Link>
                )}
                {activityItemId && (
                  <Link
                    to={`/?item=${encodeURIComponent(activityItemId)}`}
                    onClick={() => {
                      // Mark read on the way out so the bell badge updates
                      // without waiting for the next poll.  Errors are
                      // tolerable — the inbox will reconcile on next view.
                      if (!n.readAt) void onMarkOne(n.id);
                    }}
                    className="mt-1 inline-block text-[11px] font-medium text-indigo-600 hover:underline dark:text-indigo-400"
                  >
                    View task →
                  </Link>
                )}
                {isShareOffer && unread && accessToken && (
                  <ShareOfferActions
                    token={accessToken}
                    shareId={(n.payload as { shareId: string }).shareId}
                    onDecided={() => {
                      setItems((prev) =>
                        prev.map((x) =>
                          x.id === n.id
                            ? { ...x, readAt: new Date().toISOString() }
                            : x,
                        ),
                      );
                      setUnreadCount((c) => Math.max(0, c - 1));
                    }}
                  />
                )}
              </div>
              {unread && (
                <button
                  type="button"
                  onClick={() => void onMarkOne(n.id)}
                  className="shrink-0 self-center rounded px-1.5 py-0.5 text-[11px] text-slate-500 hover:bg-slate-200 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-200"
                  title="Mark read"
                  aria-label="Mark notification read"
                >
                  ✓
                </button>
              )}
            </li>
          );
        })}
      </ul>

      {items.length === 0 && !loading && (
        <p className="mt-6 text-center text-sm italic text-slate-400 dark:text-slate-500">
          No notifications yet.
        </p>
      )}

      {nextCursor && (
        <div className="mt-4 flex justify-center">
          <button
            type="button"
            onClick={() => void loadMore()}
            disabled={loading}
            className="rounded border border-slate-300 bg-white px-3 py-1 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            {loading ? "Loading…" : "Load more"}
          </button>
        </div>
      )}
    </main>
  );
}

function ContactRequestActions({
  token,
  requestId,
  onDecided,
}: {
  token: string;
  requestId: string;
  onDecided: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const act = async (action: "accept" | "decline") => {
    setBusy(true);
    setError(null);
    try {
      await decideContactRequest(token, requestId, action);
      onDecided();
    } catch (err) {
      setError(
        contactRequestErrorMessage(
          err instanceof Error ? err.message : String(err),
        ),
      );
      setBusy(false);
    }
  };

  return (
    <div className="mt-2 flex items-center gap-2">
      <button
        type="button"
        onClick={() => void act("accept")}
        disabled={busy}
        className="rounded bg-slate-900 px-2 py-0.5 text-xs font-medium text-white hover:bg-slate-800 disabled:opacity-50 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200"
      >
        Accept
      </button>
      <button
        type="button"
        onClick={() => void act("decline")}
        disabled={busy}
        className="rounded border border-slate-300 bg-white px-2 py-0.5 text-xs text-slate-700 hover:bg-slate-100 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
      >
        Decline
      </button>
      {error && <span className="text-xs text-rose-600 dark:text-rose-400">{error}</span>}
    </div>
  );
}

function ShareOfferActions({
  token,
  shareId,
  onDecided,
}: {
  token: string;
  shareId: string;
  onDecided: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const act = async (action: "accept" | "decline") => {
    setBusy(true);
    setError(null);
    try {
      await decideShare(token, shareId, action);
      onDecided();
    } catch (err) {
      setError(
        shareErrorMessage(err instanceof Error ? err.message : String(err)),
      );
      setBusy(false);
    }
  };

  return (
    <div className="mt-2 flex items-center gap-2">
      <button
        type="button"
        onClick={() => void act("accept")}
        disabled={busy}
        className="rounded bg-slate-900 px-2 py-0.5 text-xs font-medium text-white hover:bg-slate-800 disabled:opacity-50 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200"
      >
        Accept
      </button>
      <button
        type="button"
        onClick={() => void act("decline")}
        disabled={busy}
        className="rounded border border-slate-300 bg-white px-2 py-0.5 text-xs text-slate-700 hover:bg-slate-100 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
      >
        Decline
      </button>
      {error && <span className="text-xs text-rose-600 dark:text-rose-400">{error}</span>}
    </div>
  );
}

function formatWhen(iso: string): string {
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  const min = Math.floor(diff / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(iso).toLocaleDateString();
}
